import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import getDrawerItems from "./dashboardItems";
import {useDispatch, useSelector} from "react-redux";
import {useEffect, useState} from "react";
import {createAppTheme, useThemeContext} from "@theme";
import {tenantApi, userApi, api} from "@core/api";
import {
  setAssets,
  setDrawerIndex,
  setDrawerOpen,
  setTenant,
  setUsers,
} from "@core/store/slices/appSlice";
import {useColorScheme} from "@mui/material";
import {Dashboard, LoadingView} from "@components";
import {skipToken} from "@reduxjs/toolkit/query";
import {logout, setAuth} from "@core/store/slices/authSlice";
import {DrawerItem, Auth} from "@core/model";
import {DashboardRoutes} from "./DashboardRoutes";
import {useEventTenantSource, useEventUserSource} from "@hooks/useEventSource";
import {Selectors} from "@core/store/selectors";
import {LoginPage} from "../pages/login";

const AppRoutes = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const {setMode} = useColorScheme();
  const {setTheme} = useThemeContext();

  const user = useSelector(Selectors.user);
  const userRole = useSelector(Selectors.userRole);
  const tenant = useSelector(Selectors.tenant);
  const autoLogin = useSelector(Selectors.autoLogin);
  const drawerOpen = useSelector(Selectors.drawerOpen);
  const drawerIndex = useSelector(Selectors.drawerIndex);
  const colorScheme = useSelector(Selectors.colorScheme);

  const [drawerItems, setDrawerItems] = useState<(DrawerItem | JSX.Element)[]>(
    [],
  );
  const [paths, setPaths] = useState<string[]>();

  const {data: tenants} = tenantApi.useFindEntity(
    userRole === "ADMIN" ? `page=0&size=999` : skipToken,
  );

  const {
    data: tenantData,
    isLoading: isTenantLoading,
    isFetching: isTenantFetching,
    isUninitialized: isTenantUninitialized,
  } = api.useFetchTenantQuery();

  const {data: users} = userApi.useFindAllEntity(
    userRole && tenant?.id ? undefined : skipToken,
  );
  const [
    fetchAssets,
    {data: assets, isLoading: isAssetsLoading, isFetching: isAssetsFetching},
  ] = api.useLazyFetchAssetTreeQuery();

  useEffect(() => {
    if (assets) {
      dispatch(setAssets(assets));
    }
  }, [assets]);

  // Effetto per impostare il tenant iniziale se non è già presente e se l'utente è admin
  useEffect(() => {
    if (!tenant && tenants && tenants?.content.length > 0) {
      const tenant = tenants.content[0];
      dispatch(setTenant(tenant));
    }
  }, [tenants]);

  useEffect(() => {
    dispatch(setUsers(users ?? []));
  }, [users]);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

    const handleChange = () => {
      if (colorScheme === "system") {
        loadAppTheme();
        setMode(colorScheme);
      }
    };

    // Listener per il cambio
    mediaQuery.addEventListener("change", handleChange);

    // Cleanup
    return () => {
      mediaQuery.removeEventListener("change", handleChange);
    };
  }, [colorScheme]);

  useEffect(() => {
    loadAppTheme();
  }, [tenant?.primaryColor, tenant?.secondaryColor, colorScheme, setTheme]);

  useEffect(() => {
    setMode(colorScheme);
  }, [colorScheme]);

  useEffect(() => {
    if (drawerItems && drawerItems.length > 0) {
      const index =
        drawerItems.findIndex(
          d => location.pathname.indexOf((d as DrawerItem)?.path) !== -1,
        ) ?? 0;
      dispatch(setDrawerIndex(index));
    }
  }, [location.pathname, drawerItems?.length]);

  // Load tenent
  useEffect(() => {
    dispatch(setTenant(tenantData));
  }, [tenantData]);

  useEffect(() => {
    if (userRole === "ADMIN") {
      dispatch(setTenant(tenant));
    }
  }, [userRole]);

  useEffect(() => {
    if (tenant?.id && user) {
      fetchAssets();
    }
  }, [tenant?.id, user]);

  useEffect(() => {
    if (userRole && tenant) {
      const items = getDrawerItems(userRole, tenant.menu);
      setDrawerItems(items);
      setPaths(
        items
          .filter(di => (di as DrawerItem).path !== undefined)
          .map(di => (di as DrawerItem).path),
      );
    }
  }, [userRole, tenant]);

  const loadAppTheme = () => {
    if (tenant?.primaryColor && tenant?.secondaryColor) {
      const appTheme = createAppTheme(
        tenant?.primaryColor,
        tenant?.secondaryColor,
        colorScheme,
      );
      setTheme(appTheme);
    }
  };

  useEffect(() => {
    if (autoLogin && !user) {
      // @ts-expect-error fix later
      dispatch(api.endpoints.refreshToken.initiate())
        .unwrap()
        .then((response: Auth) => {
          dispatch(setAuth(response));
        })
        .catch(() => {
          dispatch(logout());
        });
    }
  }, [autoLogin, user, dispatch]);

  useEventUserSource();
  useEventTenantSource();

  const handleDrawerItemSelect = (index: number) => {
    navigate((drawerItems![index] as DrawerItem).path);
    dispatch(setDrawerIndex(index));
  };

  const isTenantUnready =
    isTenantLoading || isTenantFetching || isTenantUninitialized;

  const loading = isTenantUnready || isAssetsLoading || isAssetsFetching;

  if (autoLogin && !user) {
    return (
      <LoadingView
        loading
        logoAnimation
        sx={{bgcolor: t => t.palette.background.default}}
      />
    );
  }

  return (
    <>
      <Routes>
        <Route
          path="login"
          element={
            user ? <Navigate to={"/dashboard"} replace /> : <LoginPage />
          }
        />
        <Route
          path="dashboard/*"
          element={
            <PrivateRoute>
              <Dashboard
                appName={"PrediMain"}
                drawerItems={drawerItems ?? []}
                drawerOpen={drawerOpen}
                onDrawerOpenChange={open => dispatch(setDrawerOpen(open))}
                onDrawerItemeSelect={handleDrawerItemSelect}
                drawerItemIndex={drawerIndex ?? 0}>
                <DashboardRoutes loading={loading} paths={paths} />
              </Dashboard>
            </PrivateRoute>
          }
        />
        <Route path="*" element={<Navigate to={"/dashboard"} />} />
      </Routes>
    </>
  );
};

const PrivateRoute = (props: {children: React.ReactNode}) => {
  const location = useLocation();
  const user = useSelector(Selectors.user);

  if (user) {
    return <>{props.children}</>;
  }

  return <Navigate to={"/login"} state={{from: location}} replace />;
};

export {AppRoutes};
